import React, { useState, useEffect } from 'react';

interface SplashScreenProps {
  onComplete: () => void;
}

export const SplashScreen: React.FC<SplashScreenProps> = ({ onComplete }) => {
  const [phase, setPhase] = useState<'enter' | 'show' | 'exit'>('enter');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('show'), 100);
    const t2 = setTimeout(() => setPhase('exit'), 2800);
    const t3 = setTimeout(() => onComplete(), 3500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onComplete]);

  return (
    <div
      data-testid="splash-screen"
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden transition-opacity duration-700 ${
        phase === 'exit' ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ background: 'linear-gradient(160deg, #2b1005 0%, #4a1a08 30%, #1a0a02 70%, #0d0501 100%)' }}
    >
      {/* श्रीहरिः at top center */}
      <p
        className="absolute font-devanagari"
        style={{
          top: 18,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '0.9rem',
          color: 'rgba(255, 215, 0, 0.7)',
          letterSpacing: '0.1em',
          opacity: phase === 'enter' ? 0 : 0.85,
          transition: 'opacity 0.8s 0.2s ease',
          zIndex: 20,
        }}
      >
        श्रीहरिः
      </p>

      {/* Guru image below श्रीहरिः */}
      <img
        src="/guru-image.png"
        alt="Guru"
        className="absolute"
        style={{
          top: 42,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 180,
          height: 'auto',
          objectFit: 'contain',
          opacity: phase === 'enter' ? 0 : 0.9,
          transition: 'opacity 1s 0.4s ease',
          filter: 'drop-shadow(0 0 15px rgba(255,200,50,0.2))',
          zIndex: 20,
        }}
      />
      {/* Animated background mandala rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {[320, 480, 640].map((size, i) => (
          <div
            key={i}
            className="absolute rounded-full border opacity-0"
            style={{
              width: size, height: size,
              borderColor: `rgba(204, 85, 0, ${0.12 - i * 0.03})`,
              animation: phase !== 'enter'
                ? `splash-ring-expand 2.5s ${i * 0.3}s ease-out forwards, splash-ring-spin ${20 + i * 10}s linear infinite`
                : 'none',
            }}
          />
        ))}
      </div>

      {/* Floating divine particles */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: Math.random() * 4 + 2,
              height: Math.random() * 4 + 2,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: `radial-gradient(circle, rgba(255,${150 + Math.random() * 105},${Math.random() * 50},0.8), transparent)`,
              animation: phase !== 'enter'
                ? `splash-particle ${3 + Math.random() * 4}s ${Math.random() * 2}s ease-in-out infinite`
                : 'none',
              opacity: 0,
            }}
          />
        ))}
      </div>

      {/* Central golden glow */}
      <div
        className="absolute"
        style={{
          width: 300, height: 300,
          background: 'radial-gradient(circle, rgba(204,85,0,0.25) 0%, rgba(204,85,0,0.08) 40%, transparent 70%)',
          filter: 'blur(40px)',
          animation: phase !== 'enter' ? 'splash-glow-pulse 3s ease-in-out infinite' : 'none',
          opacity: phase !== 'enter' ? 1 : 0,
          transition: 'opacity 1s ease',
        }}
      />

      {/* Logo container with divine aura */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Outer aura ring */}
        <div
          className="absolute rounded-full"
          style={{
            width: 180, height: 180,
            top: -25, left: '50%', transform: 'translateX(-50%)',
            background: 'radial-gradient(circle, rgba(255,165,0,0.15) 0%, transparent 70%)',
            animation: phase !== 'enter' ? 'splash-aura 2s ease-in-out infinite' : 'none',
            opacity: phase !== 'enter' ? 1 : 0,
            transition: 'opacity 0.8s ease',
          }}
        />

        {/* Logo */}
        <img
          src="/golok-logo.png"
          alt="गोलोक"
          className="relative z-10"
          style={{
            width: 130, height: 'auto',
            filter: 'drop-shadow(0 0 30px rgba(204,85,0,0.5)) drop-shadow(0 0 60px rgba(255,165,0,0.2))',
            transform: phase === 'enter' ? 'scale(0.5)' : phase === 'exit' ? 'scale(1.1)' : 'scale(1)',
            opacity: phase === 'enter' ? 0 : 1,
            transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        />

        {/* App name - Hindi */}
        <h1
          className="mt-6 text-center font-devanagari"
          style={{
            fontSize: '2rem',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 30%, #CC5500 60%, #FFD700 100%)',
            backgroundSize: '200% 200%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: phase !== 'enter' ? 'splash-text-shimmer 3s ease-in-out infinite' : 'none',
            transform: phase === 'enter' ? 'translateY(20px)' : 'translateY(0)',
            opacity: phase === 'enter' ? 0 : 1,
            transition: 'all 0.8s 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            letterSpacing: '0.05em',
          }}
        >
          गोलोक दिव्य नाम कोष
        </h1>

        {/* Subtitle */}
        <p
          className="mt-2 text-center tracking-widest font-devanagari"
          style={{
            fontSize: '0.85rem',
            color: 'rgba(204, 133, 66, 0.7)',
            letterSpacing: '0.15em',
            transform: phase === 'enter' ? 'translateY(15px)' : 'translateY(0)',
            opacity: phase === 'enter' ? 0 : 0.8,
            transition: 'all 0.8s 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          आपकी वर्चुअल साधना कुटीर
        </p>

        {/* Decorative divider */}
        <div
          className="mt-4 flex items-center gap-3"
          style={{
            opacity: phase === 'enter' ? 0 : 0.6,
            transition: 'opacity 1s 0.7s ease',
          }}
        >
          <div className="h-px bg-gradient-to-r from-transparent to-amber-700/50" style={{ width: 60 }} />
          <span style={{ color: '#CC8542', fontSize: '0.75rem' }}>&#9753;</span>
          <div className="h-px bg-gradient-to-l from-transparent to-amber-700/50" style={{ width: 60 }} />
        </div>

        {/* Loading indicator */}
        <div
          className="mt-8 flex items-center gap-2"
          style={{
            opacity: phase === 'enter' ? 0 : phase === 'exit' ? 0 : 0.5,
            transition: 'opacity 0.8s 1s ease',
          }}
        >
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="rounded-full"
              style={{
                width: 5, height: 5,
                backgroundColor: '#CC5500',
                animation: `splash-dot-bounce 1.2s ${i * 0.2}s ease-in-out infinite`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom text */}
      <p
        className="absolute bottom-8 text-center"
        style={{
          fontSize: '0.6rem',
          color: 'rgba(160, 82, 45, 0.4)',
          letterSpacing: '0.15em',
          opacity: phase === 'enter' ? 0 : 0.6,
          transition: 'opacity 1s 1s ease',
        }}
      >
        &#x1F539; Golok Prem Ras &#x1F539;
      </p>

      {/* CSS Animations */}
      <style>{`
        @keyframes splash-ring-expand {
          0% { opacity: 0; transform: scale(0.5); }
          50% { opacity: 1; }
          100% { opacity: 0.4; transform: scale(1); }
        }
        @keyframes splash-ring-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes splash-glow-pulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.2); opacity: 1; }
        }
        @keyframes splash-aura {
          0%, 100% { transform: translateX(-50%) scale(1); opacity: 0.5; }
          50% { transform: translateX(-50%) scale(1.15); opacity: 0.8; }
        }
        @keyframes splash-text-shimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes splash-particle {
          0%, 100% { opacity: 0; transform: translateY(0) scale(0.5); }
          50% { opacity: 0.8; transform: translateY(-30px) scale(1); }
        }
        @keyframes splash-dot-bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
          40% { transform: scale(1.2); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
